import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUpload,
  faVideo,
  faCloud,
  faCheck,
  faTimes,
  faSpinner,
  faPlay,
  faTrash,
  faEye,
  faCopy,
  faExternalLinkAlt,
  faServer,
  faFile,
  faImage,
  faLink,
  faDownload,
  faClock
} from '@fortawesome/free-solid-svg-icons';
import '../styles/AdminUpload.css';

const AdminUpload = () => {
  const [activeTab, setActiveTab] = useState('upload');
  const [uploadQueue, setUploadQueue] = useState([]);
  const [uploadHistory, setUploadHistory] = useState([]);
  const [platforms, setPlatforms] = useState([
    {
      id: 'helvid',
      name: 'Helvid',
      url: 'https://helvid.com',
      status: 'active',
      apiKey: '',
      maxSize: '2GB',
      formats: ['mp4', 'mkv', 'avi'],
      uploadCount: 45,
      color: '#3b82f6'
    },
    {
      id: 'streamtape',
      name: 'StreamTape',
      url: 'https://streamtape.com',
      status: 'active',
      apiKey: '',
      maxSize: '5GB',
      formats: ['mp4', 'mkv', 'avi', 'mov'],
      uploadCount: 23,
      color: '#10b981'
    },
    {
      id: 'mixdrop',
      name: 'MixDrop',
      url: 'https://mixdrop.co',
      status: 'inactive',
      apiKey: '',
      maxSize: '1GB',
      formats: ['mp4', 'avi'],
      uploadCount: 12,
      color: '#ef4444'
    },
    {
      id: 'doodstream',
      name: 'DoodStream',
      url: 'https://doodstream.com',
      status: 'active',
      apiKey: '',
      maxSize: '3GB',
      formats: ['mp4', 'mkv', 'webm'],
      uploadCount: 67,
      color: '#8b5cf6'
    }
  ]);

  const [selectedPlatforms, setSelectedPlatforms] = useState(['helvid']);
  const [uploadSettings, setUploadSettings] = useState({
    quality: 'auto',
    privacy: 'public',
    autoDelete: false,
    deleteAfter: 30,
    generateThumbnail: true,
    watermark: false
  });

  // Mock upload history
  useEffect(() => {
    const mockHistory = [
      {
        id: 1,
        filename: 'Attack_on_Titan_S4_EP16.mp4',
        size: '1.2GB',
        platforms: ['helvid', 'streamtape'],
        status: 'completed',
        uploadedAt: '2024-08-07 14:30',
        links: {
          helvid: 'https://helvid.com/watch/abc123',
          streamtape: 'https://streamtape.com/v/xyz789'
        },
        thumbnail: 'https://m.media-amazon.com/images/M/MV5BNzc5MTczNDQtNDFjNi00ZDU5LWFkNzItOTE1NzQzMzdhNzU5XkEyXkFqcGdeQXVyNjc2NjA5MTU@._V1_FMjpg_UX1000_.jpg'
      },
      {
        id: 2,
        filename: 'One_Piece_EP893.mp4',
        size: '800MB',
        platforms: ['helvid', 'doodstream'],
        status: 'completed',
        uploadedAt: '2024-08-07 12:15',
        links: {
          helvid: 'https://helvid.com/watch/def456',
          doodstream: 'https://doodstream.com/d/uvw012'
        },
        thumbnail: 'https://m.media-amazon.com/images/M/MV5BODcwNWE3OTMtMDc3MS00NDFjLWE1OTAtNDU3NjgxODMxY2UyXkEyXkFqcGdeQXVyNTAyODkwOQ@@._V1_FMjpg_UX1000_.jpg'
      },
      {
        id: 3,
        filename: 'Demon_Slayer_Movie.mp4',
        size: '2.1GB',
        platforms: ['streamtape'],
        status: 'failed',
        uploadedAt: '2024-08-07 10:45',
        error: 'File size exceeds limit',
        thumbnail: 'https://m.media-amazon.com/images/M/MV5BZjZjNzI5MDctY2Y4YS00NmM4LTljMmItZTFkOTExNGI3ODRhXkEyXkFqcGdeQXVyNjc3MjQzNTI@._V1_FMjpg_UX1000_.jpg'
      }
    ];
    setUploadHistory(mockHistory);
  }, []);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const newUploads = files.map((file, index) => ({
      id: Date.now() + index,
      file,
      filename: file.name,
      size: formatFileSize(file.size),
      platforms: [...selectedPlatforms],
      status: 'pending',
      progress: 0,
      links: {},
      addedAt: new Date().toLocaleString('vi-VN')
    }));
    
    setUploadQueue(prev => [...prev, ...newUploads]);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const startUpload = (uploadId) => {
    setUploadQueue(prev =>
      prev.map(upload =>
        upload.id === uploadId
          ? { ...upload, status: 'uploading', progress: 0 }
          : upload
      )
    );

    // Simulate upload progress
    const interval = setInterval(() => {
      setUploadQueue(prev => {
        const updated = prev.map(upload => {
          if (upload.id === uploadId && upload.status === 'uploading') {
            const newProgress = Math.min(upload.progress + Math.random() * 15, 100);
            if (newProgress >= 100) {
              clearInterval(interval);
              // Move to history
              setTimeout(() => {
                const completedUpload = {
                  ...upload,
                  status: 'completed',
                  progress: 100,
                  uploadedAt: new Date().toLocaleString('vi-VN'),
                  links: upload.platforms.reduce((acc, platform) => {
                    acc[platform] = `https://${platform}.com/watch/${Math.random().toString(36).substr(2, 9)}`;
                    return acc;
                  }, {}),
                  thumbnail: 'https://m.media-amazon.com/images/M/MV5BNzc5MTczNDQtNDFjNi00ZDU5LWFkNzItOTE1NzQzMzdhNzU5XkEyXkFqcGdeQXVyNjc2NjA5MTU@._V1_FMjpg_UX1000_.jpg'
                };
                setUploadHistory(prev => [completedUpload, ...prev]);
                setUploadQueue(prev => prev.filter(u => u.id !== uploadId));
              }, 1000);
              return { ...upload, status: 'processing', progress: 100 };
            }
            return { ...upload, progress: newProgress };
          }
          return upload;
        });
        return updated;
      });
    }, 500);
  };

  const startAllUploads = () => {
    uploadQueue.filter(u => u.status === 'pending').forEach(upload => {
      setTimeout(() => startUpload(upload.id), Math.random() * 1000);
    });
  };

  const removeFromQueue = (uploadId) => {
    setUploadQueue(prev => prev.filter(upload => upload.id !== uploadId));
  };

  const removeFromHistory = (uploadId) => {
    setUploadHistory(prev => prev.filter(upload => upload.id !== uploadId));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // Show toast notification (simplified)
    alert('Đã copy link!');
  };

  const togglePlatform = (platformId) => {
    setSelectedPlatforms(prev =>
      prev.includes(platformId)
        ? prev.filter(id => id !== platformId)
        : [...prev, platformId]
    );
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return faClock;
      case 'uploading':
        return faSpinner;
      case 'processing':
        return faSpinner;
      case 'completed':
        return faCheck;
      case 'failed':
        return faTimes;
      default:
        return faFile;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return '#6b7280';
      case 'uploading':
        return '#3b82f6';
      case 'processing':
        return '#8b5cf6';
      case 'completed':
        return '#10b981';
      case 'failed':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const tabs = [
    { id: 'upload', label: 'Upload Video', icon: faUpload },
    { id: 'queue', label: 'Hàng đợi', icon: faVideo },
    { id: 'history', label: 'Lịch sử', icon: faCloud },
    { id: 'platforms', label: 'Platforms', icon: faServer }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'upload':
        return (
          <div className="upload-section">
            <div className="upload-area">
              <div className="upload-dropzone">
                <FontAwesomeIcon icon={faUpload} className="upload-icon" />
                <h3>Kéo thả file video hoặc click để chọn</h3>
                <p>Hỗ trợ MP4, MKV, AVI, MOV (Tối đa 5GB)</p>
                <input
                  type="file"
                  multiple
                  accept="video/*"
                  onChange={handleFileSelect}
                  className="file-input"
                />
                <button className="browse-btn">
                  <FontAwesomeIcon icon={faFile} />
                  Chọn file
                </button>
              </div>
            </div>

            <div className="upload-settings">
              <h4>Cài đặt Upload</h4>
              
              <div className="platforms-selection">
                <h5>Chọn platforms:</h5>
                <div className="platforms-grid">
                  {platforms.filter(p => p.status === 'active').map(platform => (
                    <label key={platform.id} className="platform-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedPlatforms.includes(platform.id)}
                        onChange={() => togglePlatform(platform.id)}
                      />
                      <span className="checkmark" style={{ borderColor: platform.color }}>
                        <FontAwesomeIcon icon={faCheck} />
                      </span>
                      <div className="platform-info">
                        <span className="platform-name">{platform.name}</span>
                        <span className="platform-limit">Max: {platform.maxSize}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="settings-grid">
                <div className="setting-group">
                  <label>Chất lượng:</label>
                  <select
                    value={uploadSettings.quality}
                    onChange={(e) => setUploadSettings(prev => ({ ...prev, quality: e.target.value }))}
                  >
                    <option value="auto">Tự động</option>
                    <option value="1080p">1080p</option>
                    <option value="720p">720p</option>
                    <option value="480p">480p</option>
                  </select>
                </div>

                <div className="setting-group">
                  <label>Quyền riêng tư:</label>
                  <select
                    value={uploadSettings.privacy}
                    onChange={(e) => setUploadSettings(prev => ({ ...prev, privacy: e.target.value }))}
                  >
                    <option value="public">Công khai</option>
                    <option value="private">Riêng tư</option>
                    <option value="unlisted">Không liệt kê</option>
                  </select>
                </div>

                <div className="setting-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={uploadSettings.generateThumbnail}
                      onChange={(e) => setUploadSettings(prev => ({ ...prev, generateThumbnail: e.target.checked }))}
                    />
                    Tạo thumbnail tự động
                  </label>
                </div>

                <div className="setting-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={uploadSettings.watermark}
                      onChange={(e) => setUploadSettings(prev => ({ ...prev, watermark: e.target.checked }))}
                    />
                    Thêm watermark
                  </label>
                </div>
              </div>
            </div>
          </div>
        );

      case 'queue':
        return (
          <div className="queue-section">
            <div className="queue-header">
              <h3>Hàng đợi upload ({uploadQueue.length})</h3>
              {uploadQueue.length > 0 && (
                <button className="start-all-btn" onClick={startAllUploads}>
                  <FontAwesomeIcon icon={faPlay} />
                  Upload tất cả
                </button>
              )}
            </div>

            <div className="queue-list">
              {uploadQueue.length === 0 ? (
                <div className="empty-state">
                  <FontAwesomeIcon icon={faVideo} />
                  <p>Chưa có file nào trong hàng đợi</p>
                </div>
              ) : (
                uploadQueue.map(upload => (
                  <div key={upload.id} className="queue-item">
                    <div className="file-info">
                      <FontAwesomeIcon icon={faVideo} className="file-icon" />
                      <div className="file-details">
                        <h4>{upload.filename}</h4>
                        <p>{upload.size} • {upload.platforms.join(', ')}</p>
                        <span className="added-time">Thêm lúc: {upload.addedAt}</span>
                      </div>
                    </div>

                    <div className="upload-progress">
                      {upload.status === 'uploading' || upload.status === 'processing' ? (
                        <>
                          <div className="progress-bar">
                            <div 
                              className="progress-fill" 
                              style={{ width: `${upload.progress}%` }}
                            ></div>
                          </div>
                          <span className="progress-text">{Math.round(upload.progress)}%</span>
                        </>
                      ) : (
                        <div className="status-badge" style={{ color: getStatusColor(upload.status) }}>
                          <FontAwesomeIcon 
                            icon={getStatusIcon(upload.status)} 
                            spin={upload.status === 'uploading' || upload.status === 'processing'}
                          />
                          {upload.status === 'pending' ? 'Chờ' : 
                           upload.status === 'uploading' ? 'Đang tải' :
                           upload.status === 'processing' ? 'Xử lý' : 'Hoàn thành'}
                        </div>
                      )}
                    </div>

                    <div className="queue-actions">
                      {upload.status === 'pending' && (
                        <button 
                          className="start-btn"
                          onClick={() => startUpload(upload.id)}
                        >
                          <FontAwesomeIcon icon={faPlay} />
                        </button>
                      )}
                      <button 
                        className="remove-btn"
                        onClick={() => removeFromQueue(upload.id)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        );

      case 'history':
        return (
          <div className="history-section">
            <div className="history-header">
              <h3>Lịch sử upload ({uploadHistory.length})</h3>
            </div>

            <div className="history-list">
              {uploadHistory.map(upload => (
                <div key={upload.id} className="history-item">
                  <div className="history-thumbnail">
                    {upload.thumbnail ? (
                      <img src={upload.thumbnail} alt={upload.filename} />
                    ) : (
                      <FontAwesomeIcon icon={faVideo} />
                    )}
                  </div>

                  <div className="history-info">
                    <h4>{upload.filename}</h4>
                    <p>{upload.size} • Upload lúc: {upload.uploadedAt}</p>
                    <div className="history-platforms">
                      {upload.platforms.map(platformId => {
                        const platform = platforms.find(p => p.id === platformId);
                        return (
                          <span key={platformId} className="platform-tag" style={{ backgroundColor: platform?.color }}>
                            {platform?.name}
                          </span>
                        );
                      })}
                    </div>
                    {upload.error && (
                      <p className="error-message">
                        <FontAwesomeIcon icon={faTimes} />
                        {upload.error}
                      </p>
                    )}
                  </div>

                  <div className="history-links">
                    {upload.status === 'completed' && upload.links && (
                      <div className="links-list">
                        {Object.entries(upload.links).map(([platform, link]) => (
                          <div key={platform} className="link-item">
                            <span className="platform-name">{platforms.find(p => p.id === platform)?.name}</span>
                            <div className="link-actions">
                              <button 
                                className="copy-btn"
                                onClick={() => copyToClipboard(link)}
                                title="Copy link"
                              >
                                <FontAwesomeIcon icon={faCopy} />
                              </button>
                              <button 
                                className="visit-btn"
                                onClick={() => window.open(link, '_blank')}
                                title="Mở link"
                              >
                                <FontAwesomeIcon icon={faExternalLinkAlt} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="history-actions">
                    <div className="status-indicator" style={{ color: getStatusColor(upload.status) }}>
                      <FontAwesomeIcon icon={getStatusIcon(upload.status)} />
                    </div>
                    <button 
                      className="delete-btn"
                      onClick={() => removeFromHistory(upload.id)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'platforms':
        return (
          <div className="platforms-section">
            <div className="platforms-header">
              <h3>Quản lý Platforms</h3>
              <p>Cấu hình các nền tảng upload video</p>
            </div>

            <div className="platforms-list">
              {platforms.map(platform => (
                <div key={platform.id} className="platform-card">
                  <div className="platform-header">
                    <div className="platform-title">
                      <h4 style={{ color: platform.color }}>{platform.name}</h4>
                      <span className={`status-badge ${platform.status}`}>
                        {platform.status === 'active' ? 'Hoạt động' : 'Tạm dừng'}
                      </span>
                    </div>
                    <a href={platform.url} target="_blank" rel="noopener noreferrer" className="platform-url">
                      <FontAwesomeIcon icon={faExternalLinkAlt} />
                      {platform.url}
                    </a>
                  </div>

                  <div className="platform-stats">
                    <div className="stat-item">
                      <span className="stat-label">Đã upload:</span>
                      <span className="stat-value">{platform.uploadCount} video</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Giới hạn:</span>
                      <span className="stat-value">{platform.maxSize}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Định dạng:</span>
                      <span className="stat-value">{platform.formats.join(', ')}</span>
                    </div>
                  </div>

                  <div className="platform-config">
                    <div className="config-group">
                      <label>API Key:</label>
                      <input
                        type="password"
                        placeholder="Nhập API key..."
                        value={platform.apiKey}
                        onChange={(e) => {
                          setPlatforms(prev =>
                            prev.map(p =>
                              p.id === platform.id ? { ...p, apiKey: e.target.value } : p
                            )
                          );
                        }}
                      />
                    </div>
                  </div>

                  <div className="platform-actions">
                    <button 
                      className={`toggle-btn ${platform.status}`}
                      onClick={() => {
                        setPlatforms(prev =>
                          prev.map(p =>
                            p.id === platform.id 
                              ? { ...p, status: p.status === 'active' ? 'inactive' : 'active' }
                              : p
                          )
                        );
                      }}
                    >
                      {platform.status === 'active' ? 'Tạm dừng' : 'Kích hoạt'}
                    </button>
                    <button className="test-btn">
                      <FontAwesomeIcon icon={faCheck} />
                      Test kết nối
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="admin-upload">
      <div className="upload-header">
        <h2>
          <FontAwesomeIcon icon={faUpload} />
          Upload Video
        </h2>
        <p>Upload video lên các nền tảng hosting</p>
      </div>

      <div className="upload-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <FontAwesomeIcon icon={tab.icon} />
            <span>{tab.label}</span>
            {tab.id === 'queue' && uploadQueue.length > 0 && (
              <span className="tab-badge">{uploadQueue.length}</span>
            )}
            {tab.id === 'history' && uploadHistory.length > 0 && (
              <span className="tab-badge">{uploadHistory.length}</span>
            )}
          </button>
        ))}
      </div>

      <div className="upload-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default AdminUpload;
